/**
 * Formular für Natürliche Person (Anlegen/Bearbeiten)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import { ArrowLeft, Save, Loader2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const ROLLEN = [
  { value: 'angehoerig', label: 'Angehöriger' },
  { value: 'arzt', label: 'Arzt/Ärztin' },
  { value: 'rechtspfleger', label: 'Rechtspfleger/in' },
  { value: 'sachbearbeiter', label: 'Sachbearbeiter/in' },
  { value: 'pflegekraft', label: 'Pflegekraft' },
  { value: 'ansprechpartner', label: 'Ansprechpartner' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const VERHAELTNISSE = [
  { value: 'keine', label: 'Kein Verwandtschaftsverhältnis' },
  { value: 'ehepartner', label: 'Ehepartner/in' },
  { value: 'kind', label: 'Kind' },
  { value: 'elternteil', label: 'Elternteil' },
  { value: 'geschwister', label: 'Geschwister' },
  { value: 'enkel', label: 'Enkel' },
  { value: 'neffe_nichte', label: 'Neffe/Nichte' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

export default function NatuerlichePersonFormPage() {
  const navigate = useNavigate();
  const { id } = useTabParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [institutionen, setInstitutionen] = useState([]);
  
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    rolle: 'sonstiges',
    firma_id: '',
    adresse: '',
    plz: '',
    ort: '',
    telefon: '',
    telefon_dienstlich: '',
    mobil: '',
    fax: '',
    email: '',
    verhaeltnis: 'keine',
    notizen: ''
  });

  useEffect(() => {
    loadInstitutionen();
    if (isEditing) {
      loadPerson();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInstitutionen = async () => {
    try {
      const result = await betreuungAPI.listJuristischePersonen({});
      setInstitutionen(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Fehler beim Laden der Institutionen:', error);
    }
  };

  const loadPerson = async () => {
    setLoading(true);
    try {
      const data = await betreuungAPI.getNatuerlichePerson(id);
      setFormData({
        vorname: data.vorname || '',
        nachname: data.nachname || '',
        rolle: data.rolle || 'sonstiges',
        firma_id: data.firma_id || '',
        adresse: data.adresse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        telefon: data.telefon || '',
        telefon_dienstlich: data.telefon_dienstlich || '',
        mobil: data.mobil || '',
        fax: data.fax || '',
        email: data.email || '',
        verhaeltnis: data.verhaeltnis || 'keine',
        notizen: data.notizen || ''
      });
    } catch (error) {
      toast.error('Person nicht gefunden');
      navigate('/personen');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vorname || !formData.nachname) {
      toast.error('Bitte Vor- und Nachname eingeben');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData };
      // Leere Strings zu null konvertieren für optionale Felder
      if (!dataToSend.firma_id) dataToSend.firma_id = null;
      if (dataToSend.verhaeltnis === 'keine') dataToSend.verhaeltnis = null;
      
      if (isEditing) {
        await betreuungAPI.updateNatuerlichePerson(id, dataToSend);
        toast.success('Person aktualisiert');
      } else {
        await betreuungAPI.createNatuerlichePerson(dataToSend);
        toast.success('Person erstellt');
      }
      navigate('/personen');
    } catch (error) {
      toast.error(error.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="nat-person-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">
          {isEditing ? 'Person bearbeiten' : 'Neue natürliche Person'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Stammdaten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Stammdaten</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vorname">Vorname *</Label>
              <Input
                id="vorname"
                value={formData.vorname}
                onChange={(e) => handleChange('vorname', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nachname">Nachname *</Label>
              <Input
                id="nachname"
                value={formData.nachname}
                onChange={(e) => handleChange('nachname', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select value={formData.rolle} onValueChange={(v) => handleChange('rolle', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {ROLLEN.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Verwandtschaftsverhältnis</Label>
              <Select value={formData.verhaeltnis} onValueChange={(v) => handleChange('verhaeltnis', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {VERHAELTNISSE.map(v => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Institution (Arbeitgeber) */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" />
            Institution / Arbeitgeber
          </h2>
          
          <div className="space-y-2">
            <Label>Ansprechpartner bei</Label>
            <Select value={formData.firma_id || 'keine'} onValueChange={(v) => handleChange('firma_id', v === 'keine' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Keine Institution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keine">Keine Institution</SelectItem>
                {institutionen.map(inst => (
                  <SelectItem key={inst.id} value={inst.id}>
                    {inst.name} ({inst.typ})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Wenn diese Person Ansprechpartner bei einer Institution ist, wählen Sie diese hier aus.
            </p>
          </div>
        </div>

        {/* Kontaktdaten */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Kontaktdaten</h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adresse">Straße und Hausnummer</Label>
              <Input
                id="adresse"
                value={formData.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plz">PLZ</Label>
                <Input
                  id="plz"
                  value={formData.plz}
                  onChange={(e) => handleChange('plz', e.target.value)}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ort">Ort</Label>
                <Input
                  id="ort"
                  value={formData.ort}
                  onChange={(e) => handleChange('ort', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefon">Telefon privat</Label>
                <Input
                  id="telefon"
                  type="tel"
                  value={formData.telefon}
                  onChange={(e) => handleChange('telefon', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefon_dienstlich">Telefon dienstlich</Label>
                <Input
                  id="telefon_dienstlich"
                  type="tel"
                  value={formData.telefon_dienstlich}
                  onChange={(e) => handleChange('telefon_dienstlich', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobil">Mobil</Label>
                <Input
                  id="mobil"
                  type="tel"
                  value={formData.mobil}
                  onChange={(e) => handleChange('mobil', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fax">Fax</Label>
                <Input
                  id="fax"
                  value={formData.fax}
                  onChange={(e) => handleChange('fax', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notizen */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Notizen</h2>
          <Textarea
            value={formData.notizen}
            onChange={(e) => handleChange('notizen', e.target.value)}
            placeholder="Zusätzliche Notizen..."
            rows={3}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
